/**
 * environmentAnchors — the rules for an environment's NAMED ANCHORS (ENG-590).
 *
 * An anchor is a named place in an environment, stored on the environment row:
 *
 *   "anchors": { "front_door": { "label": "the front door", "px": 2.15, "py": 0, "pz": -4.3 } }
 *
 * It is the shared vocabulary between three systems that cannot see each other.
 * The asset manager stores it; convorcher offers the NAMES and LABELS to the
 * character LLM; the renderer resolves a name back to a position before driving
 * kimodo.
 *
 * ── Why the shape is this ───────────────────────────────────────────────────
 * Converged with Branislav on 2026-09-16, who consumes it for ENG-1094.
 *
 *   - `label` is the reason the shape changed. The NAME is a prompt token
 *     (`kitchen_cup`); the LABEL is the prose a model is actually good with
 *     ("the blue cup on the kitchen counter"). Optional — a name alone is still
 *     addressable — but it is what makes an anchor useful to an LLM.
 *   - px/py/pz rather than [x,y,z], because `defaultPose` on the same row
 *     already spells a position that way. One endpoint, one convention.
 *   - a name-keyed MAP rather than an array of {name,…}, because the map makes
 *     names unique by construction. An array needs a duplicate check that
 *     eventually nobody runs, and a duplicate means the LLM addresses one place
 *     and the renderer walks to another.
 *
 * Not called `objectTags`, though that was the request: `tags` now means asset
 * tags (ENG-1132), and two unrelated things called tags in one API is a trap.
 *
 * ── The name rule is a MIRROR, not a local opinion ──────────────────────────
 * ANCHOR_NAME_RE is copied from avatar-asset-manager's `anchorNameRe`
 * (app/api/handlers/environment_assets.go). The two repos cannot import each
 * other, so a drift here means the UI accepts a name, the PATCH comes back 400,
 * and the user's only evidence is a save that failed for a name that looked
 * fine. Change one, change both. Same for MAX_LABEL and MAX_ANCHORS.
 *
 * Pure: no THREE, no DOM, no React, no network — it runs under `node --test`.
 */

/** Mirrors avatar-asset-manager's anchorNameRe. See the header before editing. */
export const ANCHOR_NAME_RE = /^[a-z][a-z0-9_]{0,31}$/

/** Mirrors MaxAnchorLabel. A label is injected into a prompt line, so it is a
 *  phrase rather than a description. */
export const MAX_LABEL = 120

/** Mirrors MaxAnchorsPerEnvironment. Every name is offered to the model, so a
 *  large set is a prompt problem rather than a storage one. */
export const MAX_ANCHORS = 64

/**
 * Coordinates are stored to millimetre precision.
 *
 * A raycast hands back ~15 significant digits and none past the third describe
 * anything real: the anchor is a spot a character walks to, and the splat's own
 * registration is nowhere near micrometre-exact. Rounding is what keeps a
 * re-pick of visibly the same spot from reading as a change, so the dirty badge
 * means something.
 */
const PLACES = 3
const round = (n) => Math.round(n * 10 ** PLACES) / 10 ** PLACES

/** A finite number, from a number or the string a numeric <input> hands back. */
function num(v) {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

/**
 * Normalize one anchor to its stored form, or null when it is not one.
 *
 * A MISSING coordinate is a refusal, never a 0. An anchor at the origin is a
 * plausible-looking position, which is the worst kind of wrong value to store —
 * the character would walk somewhere real and wrong.
 */
export function normalizeAnchor(a) {
  if (!a || typeof a !== 'object' || Array.isArray(a)) return null
  const px = num(a.px), py = num(a.py), pz = num(a.pz)
  if (px === null || py === null || pz === null) return null
  const label = typeof a.label === 'string' ? a.label.trim() : ''
  if ([...label].length > MAX_LABEL) return null
  // A newline would break the prompt line the label is injected into.
  if (/[\n\r]/.test(label)) return null
  const out = { px: round(px), py: round(py), pz: round(pz) }
  if (label) out.label = label
  return out
}

/** `[x, y, z]` for the renderer, which speaks THREE, not px/py/pz. */
export function anchorPosition(a) {
  return [a.px, a.py, a.pz]
}

/** An anchor from a picked world point, with an optional label. */
export function anchorFromPoint(point, label = '') {
  const [x, y, z] = Array.isArray(point) ? point : []
  return normalizeAnchor({ px: x, py: y, pz: z, label })
}

/**
 * Read the anchors off an environment asset, in any shape it reaches the UI in.
 *
 * A malformed entry is DROPPED AND NAMED rather than silently skipped: the
 * server validates every anchor it stores, so the only way to get one is a
 * hand-edited column — or a row written before the label shape landed, whose
 * `[x,y,z]` arrays no longer parse. `dropped` is what the inspector renders.
 *
 * @returns {{anchors: Record<string, {label?: string, px: number, py: number, pz: number}>, dropped: string[]}}
 */
export function readAnchors(asset) {
  const raw = asset?.anchors ?? asset?.raw?.anchors ?? asset?.raw?.Anchors ?? null
  let parsed = raw
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed) } catch { parsed = null }
  }
  if (parsed == null) return { anchors: {}, dropped: [] }
  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    console.error('[environmentAnchors] anchors is not an object, ignoring:', parsed)
    return { anchors: {}, dropped: [] }
  }
  const anchors = {}
  const dropped = []
  for (const [name, a] of Object.entries(parsed)) {
    const n = normalizeAnchor(a)
    if (!n || !ANCHOR_NAME_RE.test(name)) dropped.push(name)
    else anchors[name] = n
  }
  return { anchors, dropped }
}

/**
 * The anchors as a stably-ordered list, for rendering.
 *
 * Sorted by name rather than left in object order: the server re-marshals the
 * map on every write, so insertion order is not a property anyone can rely on,
 * and a list that reshuffles itself after each save reads as data loss.
 */
export function anchorList(anchors) {
  return Object.keys(anchors ?? {})
    .sort((a, b) => a.localeCompare(b))
    .map(name => ({ name, ...anchors[name] }))
}

/**
 * Why `name` cannot be used, or null when it can.
 *
 * `existing` is the current anchor map; `self` is the name being renamed FROM,
 * so renaming an anchor to itself is not a duplicate.
 */
export function nameError(name, existing = {}, self = null) {
  if (typeof name !== 'string' || name === '') return 'name is required'
  if (!ANCHOR_NAME_RE.test(name)) {
    return 'lowercase letters, digits and _ only, starting with a letter, max 32'
  }
  if (name !== self && Object.prototype.hasOwnProperty.call(existing, name)) {
    return `"${name}" already exists`
  }
  return null
}

/** Why `label` cannot be used, or null when it can. Empty is allowed. */
export function labelError(label) {
  if (label == null || label === '') return null
  if (typeof label !== 'string') return 'label must be text'
  if ([...label.trim()].length > MAX_LABEL) return `at most ${MAX_LABEL} characters`
  if (/[\n\r]/.test(label)) return 'must be a single line'
  return null
}

/**
 * A free name derived from `base`, e.g. `anchor`, `anchor_2`, `anchor_3`.
 *
 * Used to prefill the place-anchor flyout so the gesture works with no typing.
 * Gives up past a bound rather than looping — 999 anchors is not a case worth
 * defending, and an unbounded loop is.
 */
export function suggestName(base = 'anchor', existing = {}) {
  const root = String(base)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+$/, '')
    .slice(0, 29) || 'anchor'
  if (!Object.prototype.hasOwnProperty.call(existing, root)) return root
  for (let i = 2; i < 1000; i++) {
    const candidate = `${root}_${i}`
    if (!Object.prototype.hasOwnProperty.call(existing, candidate)) return candidate
  }
  return root
}

/**
 * `anchors` with `name` set. Returns a NEW map (the store keeps its state
 * immutable so subscribers can compare by identity).
 *
 * Throws on an invalid name or anchor: the alternative is a map that PATCHes to
 * a 400, and the caller that built it is the one that can explain why.
 */
export function withAnchor(anchors, name, value) {
  const err = nameError(name, anchors)
  // An existing name is a MOVE, not a duplicate — the caller is re-placing it.
  if (err && !err.endsWith('already exists')) throw new Error(`invalid anchor name: ${err}`)
  const a = normalizeAnchor(value)
  if (!a) throw new Error(`invalid anchor "${name}": needs finite px, py and pz`)
  if (!Object.prototype.hasOwnProperty.call(anchors ?? {}, name) &&
      Object.keys(anchors ?? {}).length >= MAX_ANCHORS) {
    throw new Error(`at most ${MAX_ANCHORS} anchors per environment`)
  }
  return { ...anchors, [name]: a }
}

/** `anchors` with `name`'s label replaced (empty clears it). */
export function withLabel(anchors, name, label) {
  const cur = anchors?.[name]
  if (!cur) throw new Error(`cannot label "${name}": no such anchor`)
  const err = labelError(label)
  if (err) throw new Error(`invalid label: ${err}`)
  return withAnchor(anchors, name, { ...cur, label: label ?? '' })
}

/** `anchors` with `name` removed (a no-op for a name that is not there). */
export function withoutAnchor(anchors, name) {
  if (!Object.prototype.hasOwnProperty.call(anchors ?? {}, name)) return anchors ?? {}
  const next = { ...anchors }
  delete next[name]
  return next
}

/** `anchors` with `from` renamed to `to`, keeping its position and label. */
export function withRenamedAnchor(anchors, from, to) {
  if (!Object.prototype.hasOwnProperty.call(anchors ?? {}, from)) {
    throw new Error(`cannot rename "${from}": no such anchor`)
  }
  if (from === to) return anchors
  const err = nameError(to, anchors, from)
  if (err) throw new Error(`invalid anchor name: ${err}`)
  const next = { ...anchors }
  const a = next[from]
  delete next[from]
  next[to] = a
  return next
}

/**
 * The value to PATCH as `anchors`.
 *
 * Re-validates the whole map rather than trusting the accumulated edits: this
 * is the last point before the wire, and the server's 400 is the only other
 * check. `{}` is a legal body — it CLEARS the anchors, which is why the caller
 * must mean it (the server treats an omitted key as a no-op and an explicit
 * null as an error).
 */
export function serializeAnchors(anchors) {
  const out = {}
  for (const [name, a] of Object.entries(anchors ?? {})) {
    if (!ANCHOR_NAME_RE.test(name)) throw new Error(`invalid anchor name "${name}"`)
    const n = normalizeAnchor(a)
    if (!n) throw new Error(`invalid anchor "${name}": needs finite px, py and pz`)
    out[name] = n
  }
  return out
}

/** Value equality over two anchor maps, for dirty tracking. */
export function anchorsEqual(a, b) {
  const ka = Object.keys(a ?? {})
  const kb = Object.keys(b ?? {})
  if (ka.length !== kb.length) return false
  for (const k of ka) {
    const x = a[k], y = b?.[k]
    if (!y) return false
    if (x.px !== y.px || x.py !== y.py || x.pz !== y.pz) return false
    if ((x.label ?? '') !== (y.label ?? '')) return false
  }
  return true
}
